import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, FactGrid, Callout, CodeSample, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';
import { CantonParty, ExerciseResult, IouChoice, controllersOf, exerciseIou, initialIouLedger } from './cantonModel';
import { PartyChip, PartyPicker, Verdict } from './cantonVisuals';

export const meta = {
    title: 'Choices & Controllers',
    tagline: 'Who\'s allowed to act on a contract is declared upfront, not checked at call time.',
};

const IOU_WITH_CHOICES = `
template Iou
  with
    issuer : Party
    owner  : Party
    amount : Decimal
  where
    signatory issuer
    observer owner
    ensure amount > 0.0

    choice Transfer : ContractId Iou         -- consuming (the default)
      with newOwner : Party
      controller owner
      do create this with owner = newOwner

    choice Split : (ContractId Iou, ContractId Iou)
      with splitAmount : Decimal
      controller owner
      do
        a <- create this with amount = splitAmount
        b <- create this with amount = amount - splitAmount
        return (a, b)

    nonconsuming choice Describe : Text      -- contract stays active
      controller owner
      do return (show amount)

    -- Every template also gets an implicit Archive choice,
    -- controlled by its signatories (here: issuer).
`;

const SOLIDITY_TRANSFER = `
contract Iou {
    address public issuer;
    address public owner;

    // Anyone can call this. Access control is ordinary code
    // in the body, and forgetting it is a classic bug.
    function transfer(address newOwner) external {
        require(msg.sender == owner, "not owner");
        owner = newOwner;
    }
}
`;

const DAML_TRANSFER = `
choice Transfer : ContractId Iou
  with newOwner : Party
  controller owner      -- checked by the ledger before
  do                    -- the body ever runs
    create this with owner = newOwner
`;

const ACTORS: CantonParty[] = ['Alice', 'Bob', 'Bank', 'Carol'];

const ChoiceSimulator = () => {
    const colors = useColors();
    const [ledger, setLedger] = useState(initialIouLedger);
    const [selectedId, setSelectedId] = useState(1);
    const [actor, setActor] = useState<CantonParty>('Alice');
    const [choice, setChoice] = useState<IouChoice>('Transfer');
    const [newOwner, setNewOwner] = useState<CantonParty>('Bob');
    const [result, setResult] = useState<ExerciseResult | null>(null);

    const selected = ledger.contracts.find((item) => item.id === selectedId) ?? ledger.contracts[0];
    const archived = result?.ok ? ledger.contracts.find((item) => item.id === result.archived) : undefined;
    const archivedIssuer = archived?.issuer;
    const archivedOwner = archived?.owner;

    const submit = () => {
        const outcome = exerciseIou(ledger, selected.id, choice, actor, newOwner);
        setResult(outcome);
        if (outcome.ok) {
            setLedger(outcome.ledger);
            if (outcome.created.length) setSelectedId(outcome.created[0]);
        }
    };

    const reset = () => {
        setLedger(initialIouLedger());
        setSelectedId(1);
        setResult(null);
    };

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '18px' }}>
            <Panel>
                <MiniLabel>Ledger: click a contract to target it</MiniLabel>
                <Box sx={{ display: 'grid', gap: '8px', mb: 2.5 }}>
                    {ledger.contracts.map((contract) => {
                        const isSelected = contract.id === selected.id;
                        return (
                            <ButtonBase
                                key={contract.id}
                                onClick={() => setSelectedId(contract.id)}
                                aria-pressed={isSelected}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                    textAlign: 'left',
                                    p: '10px 12px',
                                    borderRadius: '8px',
                                    border: `1px solid ${isSelected ? colors.accent.cyan : colors.border.subtle}`,
                                    backgroundColor: isSelected ? colors.accent.cyanGlow : colors.bg.primary,
                                    opacity: contract.active ? 1 : 0.55,
                                }}
                            >
                                <Typography sx={{
                                    color: colors.text.primary,
                                    fontSize: '0.88rem',
                                    textDecoration: contract.active ? 'none' : 'line-through',
                                }}>
                                    <Box component="span" sx={{ fontFamily: '"Share Tech Mono", monospace', color: colors.text.muted, mr: 1 }}>#{contract.id}</Box>
                                    Iou ${contract.amount} · owner {contract.owner}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: contract.active ? colors.accent.green : colors.text.muted }}>
                                    {contract.active ? 'ACTIVE' : 'ARCHIVED'}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>
                <MiniLabel>Choice</MiniLabel>
                <SegmentedToggle<IouChoice>
                    ariaLabel="Choice"
                    value={choice}
                    onChange={setChoice}
                    options={[{ value: 'Transfer', label: 'Transfer' }, { value: 'Split', label: 'Split in half' }, { value: 'Archive', label: 'Archive' }]}
                />
                <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem', mt: 1, mb: 2 }}>
                    controller on #{selected.id}: {controllersOf(choice, selected).join(', ')}
                </Typography>
                {choice === 'Transfer' && (
                    <Box sx={{ mb: 2 }}>
                        <MiniLabel>newOwner</MiniLabel>
                        <PartyPicker label="New owner" parties={['Alice', 'Bob', 'Carol']} selected={[newOwner]} onToggle={setNewOwner} />
                    </Box>
                )}
                <MiniLabel>Submit as</MiniLabel>
                <PartyPicker label="Submitting party" parties={ACTORS} selected={[actor]} onToggle={setActor} />
                <Box sx={{ display: 'flex', gap: '10px', mt: 2.5, flexWrap: 'wrap' }}>
                    <Button variant="outlined" onClick={submit} sx={{ borderColor: colors.accent.cyan, color: colors.accent.cyan }}>
                        Exercise {choice} as {actor}
                    </Button>
                    <Button variant="outlined" onClick={reset} sx={{ borderColor: colors.border.default, color: colors.text.secondary }}>
                        Reset ledger
                    </Button>
                </Box>
            </Panel>
            <Panel borderColor={result ? (result.ok ? colors.accent.green : colors.accent.orange) : undefined}>
                <MiniLabel>Transaction result</MiniLabel>
                {!result && (
                    <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem', lineHeight: 1.65 }}>
                        Submit a choice to see what the ledger does. Things to try: transfer #1 as Alice, then try
                        to transfer #1 again. Try Transfer as Bank. Try anything as Carol. Then try Archive as Bank
                        and think about what that means for Alice.
                    </Typography>
                )}
                {result && result.ok && (
                    <Verdict ok title="Committed">
                        #{result.archived} was <strong>archived</strong>
                        {result.created.length > 0
                            ? <> and replaced by {result.created.map((id) => `#${id}`).join(' and ')}. The old contract ID is dead forever; anything still pointing at it will fail.</>
                            : <> and nothing replaced it. {archivedIssuer}, the only signatory, just erased its own debt to {archivedOwner} without asking. That's the implicit Archive choice at work, and a good reason real designs make the owner a signatory too.</>}
                    </Verdict>
                )}
                {result && !result.ok && (
                    <Verdict ok={false} title={{ unauthorized: 'Rejected: not a controller', archived: 'Rejected: contract not active', 'not-visible': 'Rejected: contract not found' }[result.reason]}>
                        {result.message}
                    </Verdict>
                )}
                <Box sx={{ mt: 2.5, display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography sx={{ color: colors.text.muted, fontSize: '0.78rem' }}>stakeholders of #{selected.id}</Typography>
                    <PartyChip party={selected.issuer} />
                    {selected.owner !== selected.issuer && <PartyChip party={selected.owner} />}
                </Box>
            </Panel>
        </Box>
    );
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what a "choice" represents and who can exercise it
//   Q1 - explicit controller declaration vs. Ethereum's caller-checks-at-runtime pattern
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 6 of 8" title="Contracts define what can happen to them next">
            <Prose>
                A DAML contract isn't just data. Its template defines <strong>choices</strong>: the specific actions
                that can ever be taken on it. Each choice names its <strong>controllers</strong>, the parties allowed
                to exercise it, and has a body describing what happens when they do: creating new contracts,
                exercising choices on other contracts, or reading them. If an action isn't expressed as a choice,
                there's simply no way to perform it.
            </Prose>
            <Prose>
                Because contracts are immutable, most choices are <strong>consuming</strong> (the default): exercising
                the choice archives the contract, and its body typically creates a new one reflecting the updated
                agreement. A <strong>nonconsuming</strong> choice leaves the contract active, which is useful for
                actions that can happen many times, like reading data or issuing something repeatedly.
            </Prose>
            <CodeSample language="daml" caption="The Iou again, now with its choices" code={IOU_WITH_CHOICES} />
            <FactGrid columns={3} items={[
                ['Choice', 'A named action on a contract, with typed arguments and a return value, like a method.'],
                ['Controller', 'The party (or parties) who must authorize exercising the choice. Usually a field of the contract.'],
                ['Consuming', 'Exercising archives the contract. It can never be used again; its ID becomes invalid.'],
            ]} />
            <Prose>
                Now run it. The simulator below enforces exactly the rules of the template above.
            </Prose>
            <ChoiceSimulator />
        </StageSection>
    )),

    question(0),

    content('explicit-authorization', () => (
        <StageSection title="Authorization declared on the contract, not checked inside a function body">
            <Prose>
                An Ethereum function is callable by anyone by default. If the contract wants to restrict who can
                successfully call it, that check has to be written into the function's own logic, usually a{' '}
                <code>require</code> on <code>msg.sender</code>. Forget it, or get it subtly wrong, and anyone can
                call the function: some of the most expensive hacks in Ethereum history were exactly that.
            </Prose>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: { xs: 0, lg: '18px' } }}>
                <CodeSample language="solidity" caption="Solidity: check at call time" code={SOLIDITY_TRANSFER} />
                <CodeSample language="daml" caption="DAML: declared on the choice" code={DAML_TRANSFER} />
            </Box>
            <Prose>
                A DAML choice works the other way around. Its controllers are part of the template's definition,
                and the concrete parties come from the contract's own fields, fixed when the contract was created and
                agreed to by its signatories. The ledger checks that the controllers authorized the exercise{' '}
                <em>before</em> the body runs, and every involved node re-checks it during validation. There is no{' '}
                <code>msg.sender</code> check to forget.
            </Prose>
            <Callout tone="security" title="Where DAML authorization still goes wrong">
                Declared doesn't mean correct. Inside a choice body, the transaction carries the combined authority
                of the contract's signatories <em>and</em> the choice's controllers, so a choice effectively
                delegates the signatories' power to its controller, within whatever the body allows. Common bugs
                follow from that:
                <Box component="ul" sx={{ pl: '20px', mt: 1, mb: 0 }}>
                    <li><strong>Flexible controllers</strong>: <code>controller p</code> where <code>p</code> is a choice argument lets any party who can see the contract name itself as controller.</li>
                    <li><strong>Unchecked arguments</strong>: a Split without <code>ensure amount &gt; 0.0</code> would happily create a negative Iou from a too-large <code>splitAmount</code>.</li>
                    <li><strong>Wrong consumption</strong>: a nonconsuming choice that should consume can be replayed; a signatory-only Archive lets one side walk away, as the simulator showed.</li>
                </Box>
            </Callout>
            <Prose>
                Getting those declarations right, and understanding exactly who ends up authorized to do what, is the
                class of issue the dedicated DAML authorization track explores in depth.
            </Prose>
        </StageSection>
    )),

    question(1),
];
